import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, MapPin, Plus, Check, Loader2, X, ShieldAlert, ExternalLink } from "lucide-react";

const WINDOWS = [[30, "30D"], [60, "60D"], [90, "90D"], [0, "ALL"]];
const DISC_COLOR = { police: "#3B82F6", fire: "#EF4444", ems: "#10B981" };

export const FrequentLocations = ({ compact = false, limit = 25 }) => {
  const [days, setDays] = useState(compact ? 30 : 90);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [report, setReport] = useState(null);
  const [repLoading, setRepLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await client.get("/frequent-locations", { params: { days, limit } }); setRows(data.locations || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const addKnown = async (addr) => {
    setAdding(addr);
    try {
      await client.post("/premises", { address: addr, apt: "", building_wide: true, responsible_party: "", contacts: "", access_notes: "", fire_ems_staging: "" });
      toast.success("Added to Known Addresses");
      load();
      if (report && report.address === addr) openReport(addr);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add address"); }
    finally { setAdding(""); }
  };

  const openReport = async (addr) => {
    setRepLoading(true);
    setReport({ address: addr, loading: true });
    try { const { data } = await client.get("/location-report", { params: { address: addr } }); setReport(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load report"); setReport(null); }
    finally { setRepLoading(false); }
  };

  return (
    <div data-testid="frequent-locations" className="border border-[#2C2D35] bg-[#141518]">
      <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-2 flex-wrap">
        <span className="font-head text-xs font-bold flex items-center gap-1.5 text-[#F59E0B]"><TrendingUp size={13} /> FREQUENT CALL LOCATIONS</span>
        <div className="ml-auto flex gap-1">
          {WINDOWS.map(([d, lab]) => (
            <button key={d} data-testid={`freq-window-${d}`} onClick={() => setDays(d)}
              className={`px-2 py-0.5 text-[10px] font-head border transition-colors ${days === d ? "bg-[#F59E0B] border-[#F59E0B] text-black" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF] hover:text-white"}`}>{lab}</button>
          ))}
        </div>
      </div>
      <div className={compact ? "max-h-64 overflow-y-auto" : ""}>
        {loading && <div className="p-3 flex items-center gap-2 text-[11px] font-mono text-[#9CA3AF]"><Loader2 size={13} className="animate-spin" /> Loading…</div>}
        {!loading && rows.length === 0 && <div className="p-3 text-[11px] font-mono text-[#6B7280]">No repeat-call locations in this window.</div>}
        {!loading && rows.map((r, i) => (
          <div key={r.address} data-testid={`freq-loc-${i}`} className="px-3 py-2 border-b border-[#2C2D35] last:border-0 flex items-start gap-2 hover:bg-[#0d0e11] transition-colors">
            <span className="font-head font-bold text-sm text-[#F59E0B] w-6 shrink-0 tabular-nums">{i + 1}</span>
            <button data-testid={`freq-drill-${i}`} onClick={() => openReport(r.address)} className="flex-1 text-left min-w-0" title="View full location report">
              <div className="text-sm font-head flex items-center gap-1.5 flex-wrap hover:text-[#F59E0B] transition-colors">
                <MapPin size={12} className="text-[#9CA3AF]" /> {r.address}
                {r.is_known && <span className="font-mono text-[9px] px-1 rounded bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/50">KNOWN</span>}
              </div>
              <div className="font-mono text-[10px] text-[#9CA3AF] mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span className="text-[#EF4444] font-bold">{r.count} calls</span>
                {(r.disciplines || []).map((d) => <span key={d} className="px-1 rounded" style={{ background: (DISC_COLOR[d] || "#6B7280") + "22", color: DISC_COLOR[d] || "#9CA3AF" }}>{d}</span>)}
                {r.last_call && <span>· last {new Date(r.last_call).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>}
              </div>
              {!compact && (r.types || []).length > 0 && (
                <div className="font-mono text-[9px] text-[#6B7280] mt-0.5 truncate">{r.types.join(" · ")}</div>
              )}
            </button>
            {!r.is_known && (
              <button data-testid={`freq-add-${i}`} onClick={() => addKnown(r.address)} disabled={adding === r.address} title="Add to Known Addresses"
                className="shrink-0 flex items-center gap-1 px-2 py-1 text-[10px] font-head bg-[#3B82F6] text-white rounded hover:bg-[#2563EB] transition-colors disabled:opacity-50">
                {adding === r.address ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add
              </button>
            )}
            {r.is_known && <Check size={14} className="text-[#10B981] shrink-0 mt-1" />}
          </div>
        ))}
      </div>

      {report && (
        <div data-testid="location-report-modal" className="fixed inset-0 z-[999] bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={() => setReport(null)}>
          <div className="w-full max-w-2xl bg-[#0d0e11] border border-[#2C2D35] my-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2C2D35]">
              <MapPin size={15} className="text-[#F59E0B]" />
              <span className="font-head font-bold text-sm text-white truncate">{report.address}</span>
              <button data-testid="location-report-close" onClick={() => setReport(null)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={18} /></button>
            </div>
            {repLoading || report.loading ? (
              <div className="p-6 flex items-center gap-2 text-sm font-mono text-[#9CA3AF]"><Loader2 size={15} className="animate-spin" /> Loading location report…</div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span data-testid="location-report-total" className="font-head font-bold text-2xl text-[#EF4444]">{report.total}</span>
                  <span className="font-mono text-[11px] text-[#9CA3AF] uppercase">total calls for service</span>
                  <a href={`/premise?address=${encodeURIComponent(report.address)}`} data-testid="location-report-premise-link"
                     className="ml-auto flex items-center gap-1 text-[11px] font-head text-[#3B82F6] hover:underline">Open Premise Intelligence <ExternalLink size={11} /></a>
                </div>

                {(report.hazards || []).length > 0 && (
                  <div className="border border-[#DC2626]/50 bg-[#DC2626]/10 rounded p-2" data-testid="location-report-hazards">
                    <div className="font-head text-[11px] font-bold text-[#F87171] flex items-center gap-1.5 mb-1"><ShieldAlert size={12} /> HAZARDS ON FILE ({report.hazards.length})</div>
                    {report.hazards.map((h, i) => (
                      <div key={i} className="font-mono text-[10px] text-[#FCA5A5]">• {h.type}{h.reliability ? ` (${h.reliability})` : ""}{h.detail ? ` — ${h.detail}` : ""}</div>
                    ))}
                  </div>
                )}

                {(report.top_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="font-mono text-[10px] text-[#9CA3AF]">Top call types:</span>
                    {report.top_types.map((t, i) => (
                      <span key={i} className="font-mono text-[10px] px-2 py-0.5 bg-[#141518] border border-[#2C2D35] text-[#D1D5DB]">{t.type} ({t.count})</span>
                    ))}
                  </div>
                )}

                <div className="border border-[#2C2D35]">
                  <div className="px-3 py-1.5 border-b border-[#2C2D35] bg-[#141518] font-head text-[11px] font-bold text-[#9CA3AF]">CALL HISTORY ({report.total})</div>
                  <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#2C2D35]">
                    {report.calls.length === 0 && <div className="p-3 text-[11px] font-mono text-[#6B7280]">No calls recorded.</div>}
                    {report.calls.map((c, i) => (
                      <div key={i} data-testid={`location-report-call-${i}`} className="px-3 py-2">
                        <div className="flex items-center gap-2 font-mono text-[10px] text-[#9CA3AF] flex-wrap">
                          <span className="text-[#f87171]">P{c.priority}</span>
                          <span className="text-[#6ea8ff]">{c.cc_number}</span>
                          <span className="uppercase" style={{ color: DISC_COLOR[c.discipline] || "#9CA3AF" }}>{c.discipline}</span>
                          <span>{(c.status || "").toUpperCase()}</span>
                          <span className="ml-auto">{c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { hour12: false }) : "—"}</span>
                        </div>
                        <div className="text-sm font-head text-white">{c.call_type_label}</div>
                        <div className="font-mono text-[10px] text-[#9CA3AF]">
                          {c.disposition ? `Disposition: ${c.disposition}` : "No disposition"}{(c.assigned_units || []).length ? ` · Units: ${c.assigned_units.join(", ")}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FrequentLocations;
